import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import { TestIds } from "@storyboard/web-test-ids/testIds";
import { formatDialNumber } from "@/hooks/dispatch/driverPhone";
import { zinc } from "@/ui/tokens";

/**
 * Places a call to a number through the RingCentral app.
 *
 * `window.open` rather than a same-window link, so the Dispatch page never navigates: a browser hands the scheme to the
 * operating system, and Electron's window-open handler passes it to `shell.openExternal`. `noopener` leaves whatever
 * the browser opens with no handle back to the page.
 *
 * @param e164 - The number to call, in E.164.
 */
function callWithRingCentral(e164: string): void {
  window.open(`rcapp://r/call?number=${encodeURIComponent(e164)}`, "_blank", "noopener");
}

/**
 * The Dispatch popover header's Call button, which calls the driver's number through RingCentral.
 *
 * Renders nothing without a number: a driver with no dialable number gets no button rather than a disabled one.
 */
export default function DispatchCallButton({ number }: { number: string | null }) {
  if (number === null) return null;
  return (
    <Tooltip title={`Call ${formatDialNumber(number)} in RingCentral`}>
      <IconButton
        aria-label="Call"
        data-sb={TestIds.dispatch.popoverCall}
        onClick={() => callWithRingCentral(number)}
        sx={{
          flex: "none",
          width: 28,
          height: 28,
          borderRadius: 1,
          border: `1px solid ${zinc[700]}`,
          color: zinc[300],
          "&:hover": { color: "white", bgcolor: zinc[800] },
        }}
      >
        <PhoneOutlinedIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>
  );
}
